import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, FileText, BarChart3,
  FileSpreadsheet, Settings, LogOut,
  ChevronLeft, ChevronRight, GraduationCap, ClipboardCheck
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { DraftyMascot } from './DraftyMascot';
import Eyebrow from './Eyebrow';

export const Sidebar = ({ activeTab, setActiveTab, collapsed, setCollapsed }) => {
  const { profile, isTeacher, signOut } = useAuth();

  const studentTabs = [
    { id: 'assignments', label: 'Assignments', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const teacherTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'classroom', label: 'Classes', icon: GraduationCap },
    { id: 'assignments', label: 'Assignments', icon: FileText },
    { id: 'flowgrade', label: 'FlowGrade', icon: ClipboardCheck },
    { id: 'rubric', label: 'Rubrics', icon: FileSpreadsheet },
    { id: 'analytics', label: 'Insights', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const tabs = isTeacher ? teacherTabs : studentTabs;

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 72 : 256 }}
      className="h-screen bg-slate-800 border-r border-slate-700 flex flex-col"
    >
      {/* Logo */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <DraftyMascot size="sm" className="!w-8 !h-8" />
            <span className="font-heading font-bold text-white">DraftFlow</span>
          </div>
        )}
        {collapsed && <DraftyMascot size="sm" className="!w-8 !h-8 mx-auto" />}
        <button
          data-testid="collapse-sidebar"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 hover:bg-slate-700 rounded text-slate-400"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Role Badge */}
      <div className="p-3 border-b border-slate-700">
        <div className={`flex ${collapsed ? 'justify-center' : ''}`}>
          <span className={`status-chip ${
            isTeacher
              ? 'status-chip--primary'
              : 'status-chip--gold'
          }`}>
            {collapsed ? (isTeacher ? 'T' : 'S') : (isTeacher ? 'Teacher' : 'Student')}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav aria-label="Main navigation" className="flex-1 p-3 space-y-1 overflow-y-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={`nav-${tab.id}`}
            aria-label={tab.label}
            aria-current={activeTab === tab.id ? 'page' : undefined}
            onClick={() => setActiveTab(tab.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-flow-blue/20 text-flow-blue-hover'
                : 'text-slate-400 hover:bg-slate-700 hover:text-white'
            }`}
          >
            <tab.icon size={20} />
            {!collapsed && <span className="font-medium">{tab.label}</span>}
          </button>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-slate-700">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <img
            src={profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.email}`}
            alt="Avatar"
            className="w-10 h-10 rounded-full bg-slate-700"
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{profile?.full_name}</p>
              <p className="text-xs text-slate-400 capitalize">{profile?.role}</p>
            </div>
          )}
        </div>
        <button
          data-testid="sign-out-btn"
          onClick={signOut}
          className={`w-full mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-red-400 transition-colors ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm">Sign Out</span>}
        </button>
      </div>
    </motion.aside>
  );
};

export const TopBar = ({ title, subtitle, eyebrow }) => {
  return (
    <header className="h-16 bg-slate-800/50 backdrop-blur-xl border-b border-slate-700 px-6 flex items-center">
      <div>
        {eyebrow && <div className="leading-none"><Eyebrow label={eyebrow.label} tone={eyebrow.tone} /></div>}
        <h1 className="font-heading text-xl font-semibold text-white">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
      </div>
    </header>
  );
};

export const DashboardLayout = ({ children, title, subtitle }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-slate-900">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title={title} subtitle={subtitle} />
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
